import { isFlightsView } from './sidebar/flightArtifact';

// Pressing "See return times" answers the flights card but the real work
// (looking up the returns and making a new card) happens afterwards, so the
// card header must not say the task is complete yet.
export function isAwaitingReturnTimes(
  artifact: { status: string; view?: string | null; userResponse?: unknown },
  laterActivity: boolean,
): boolean {
  if (
    artifact.status !== 'actioned' ||
    !isFlightsView(artifact.view ?? undefined)
  ) {
    return false;
  }
  let response: unknown = artifact.userResponse;
  if (typeof response === 'string') {
    try {
      response = JSON.parse(response);
    } catch {
      return false;
    }
  }
  const key = (response as { desiredAction?: { key?: string } } | null)
    ?.desiredAction?.key;
  return key === 'find_more' && !laterActivity;
}

// Turns a message timestamp (ms number, numeric string or ISO string) into ms.
function toMs(value: unknown): number | null {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = /^\d+$/.test(value) ? parseInt(value, 10) : Date.parse(value);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

// True when something from the secretary (a message or a tool step) comes
// after the item at `index` in the thread AND after `sinceMs` (the moment the
// user pressed "See return times"). The secretary's own summary of the card,
// written before the press, must not count as the follow-up; only activity
// after the press (the returns card, or a "no returns found" reply) does.
// Items with no timestamp count as later activity so the header can never
// stay stuck on "Finding return times…".
export function hasLaterAgentActivity(
  items: unknown[],
  index: number,
  sinceMs: number | null = null,
) {
  const isAfterPress = (item: unknown) => {
    if (sinceMs === null) return true;
    const ts = toMs((item as { timestamp?: unknown } | null)?.timestamp);
    return ts === null || ts > sinceMs;
  };
  const isToolGroup = (item: unknown): item is { tools: unknown[] } =>
    !!item && typeof item === 'object' && 'tools' in item && 'family' in item;
  // The returns card often lands in the SAME tool group as the card that was
  // pressed (both come from one sub-thread step), so look at that group's own
  // later tool steps too — but only ones stamped after the press, or the
  // card's own step would count.
  const own = items[index];
  const laterInOwnGroup =
    sinceMs !== null &&
    isToolGroup(own) &&
    (own.tools ?? []).some((tool) => {
      const ts = toMs((tool as { timestamp?: unknown } | null)?.timestamp);
      return ts !== null && ts > sinceMs;
    });
  if (laterInOwnGroup) return true;
  return items.slice(index + 1).some((item) => {
    if (isToolGroup(item)) {
      return (item.tools ?? []).some(isAfterPress);
    }
    const role = (item as { role?: string } | null)?.role;
    return role !== 'user' && isAfterPress(item);
  });
}
